import React, { useCallback, useEffect, useState } from 'react';
import Search from '../../shared/components/ui/Search';
import Table from '../../shared/components/layout/Table';
import { transformData } from '../../super-admin/utils/transformData';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  approveUnderConsideration,
  getUnderConsiderationByProjectID,
  deleteUnderConsideration,
  handleCheckUCDelete,
  getLeadsForPicker,
  createUnderConsideration
} from '../services/api/implementation/impl';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  buildReturnToFromLocation,
  navigateWithReturn
} from '../../shared/navigation';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import NoDataFound from '../components/layout/NoDataFound';
import PaginationComponent from '../../shared/components/PaginationComponent';
import toast from 'react-hot-toast';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useForm } from 'react-hook-form';
import FormInput from '../../shared/components/ui/FormInput';
import DeleteConfirmationModal from '../components/ui/DeleteConfirmationModal';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import Loader from '../../shared/components/ui/Loader';
import { UnderConsiderationCreate } from '../components/forms/UnderConsiderationCreate';
import Headroom from 'react-headroom';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const UnderConsiderationList = ({ forcedProjectID = null }) => {
  const userId = Cookies.get('userId');
  const userDesignationLevel = Cookies.get('userDesignationLevel');
  const {
    register,
    formState: { errors: selectErrors },
    setValue
  } = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const [projectID, setProjectID] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [activeFilters, setActiveFilters] = useState(() => {
    // Load saved filters from localStorage on component mount
    const savedFilters = localStorage.getItem('underConsiderationListFilters');
    return savedFilters
      ? JSON.parse(savedFilters)
      : {
        project: null,
        status: null
      };
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { collapsed } = useSidebar();
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulk, setIsBulk] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSelectLeadModalOpen, setIsSelectLeadModalOpen] = useState(false);
  const [isCloseDealModalOpen, setIsCloseDealModalOpen] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [leadPickerSearch, setLeadPickerSearch] = useState('');
  const [debouncedLeadSearch, setDebouncedLeadSearch] = useState('');

  const {
    data: underConsiderationData,
    refetch: refetchData,
    isFetching
  } = useQuery({
    queryKey: ['underConsideration', page, projectID, statusFilter, searchQuery],
    queryFn: () =>
      getUnderConsiderationByProjectID(projectID, page, limit, searchQuery, statusFilter)
  });

  const totalPages = underConsiderationData?.meta
    ? Math.ceil(underConsiderationData?.meta.total / limit)
    : 1;

  // Track page view
  useEffect(() => {
    mixpanel.track('Under Consideration List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchData();
  }, []);

  const {
    data: leadsData,
    isLoading: isLeadsLoading,
    refetch: refetchLeads
  } = useQuery({
    queryKey: ['leadsPicker', 'uc', debouncedLeadSearch, projectID],
    queryFn: () =>
      getLeadsForPicker({
        page: 1,
        limit: 100,
        search: debouncedLeadSearch,
        excludeDeal: true,
        excludeUc: true,
        projectId: projectID || null
      }),
    enabled: isSelectLeadModalOpen,
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (!isSelectLeadModalOpen) {
      setLeadPickerSearch('');
      setDebouncedLeadSearch('');
      return;
    }
    const t = setTimeout(() => setDebouncedLeadSearch(leadPickerSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [leadPickerSearch, isSelectLeadModalOpen]);

  // Check localStorage first, then default to first project
  React.useEffect(() => {
    if (projects && isInitialLoad) {
      if (forcedProjectID) {
        const forcedProject = projects.find((p) => String(p.id) === String(forcedProjectID));
        setProjectID(forcedProjectID);
        setActiveFilters((prev) => ({
          ...prev,
          project: forcedProject?.name || prev.project || null
        }));
        setIsInitialLoad(false);
        return;
      }

      // Only restore filters on initial load
      if (activeFilters.project) {
        const savedProject = projects.find((p) => p.name === activeFilters.project);
        if (savedProject) {
          setProjectID(savedProject.id);
        } else {
          setProjectID(null);
          setActiveFilters((prev) => ({ ...prev, project: null }));
          saveFiltersToStorage({ ...activeFilters, project: null });
        }
      } else {
        setProjectID(null);
        setActiveFilters((prev) => ({ ...prev, project: null }));
        saveFiltersToStorage({ ...activeFilters, project: null });
      }

      // Restore status filter
      if (activeFilters.status) {
        setStatusFilter(activeFilters.status === 'Approved' ? true : false);
      } else {
        setStatusFilter(null);
        setActiveFilters((prev) => ({ ...prev, status: null }));
        saveFiltersToStorage({ ...activeFilters, status: null });
      }

      setIsInitialLoad(false);
    }
  }, [projects, isInitialLoad, forcedProjectID]);

  const handleUnderConsiderationData = (item) => {
    return {
      id: item?.id,
      name: item?.lead?.user?.name || '---',
      Unit_Name: `${item?.unit?.unitName || ''}`.trim() || '---',
      project: item?.unit?.project?.name || '---',
      Customer_Proposal:
        item.customer_proposal !== 0 &&
          item.customer_proposal !== null &&
          item.customer_proposal !== undefined &&
          item.customer_proposal !== '' &&
          !isNaN(item.customer_proposal)
          ? `₹${parseInt(item.customer_proposal).toLocaleString('en-IN')}`
          : '₹0',
      Plc_Price:
        item.plc_price !== 0 &&
          item.plc_price !== null &&
          item.plc_price !== undefined &&
          item.plc_price !== '' &&
          !isNaN(item.plc_price)
          ? `₹${parseInt(item.plc_price).toLocaleString('en-IN')}`
          : '₹0',
      GST:
        item.gst !== 0 &&
          item.gst !== null &&
          item.gst !== undefined &&
          item.gst !== '' &&
          !isNaN(item.gst)
          ? `₹${parseInt(item.gst).toLocaleString('en-IN')}`
          : '₹0',
      Total_Cost: `₹${(parseInt(item.plc_price) || 0) +
        (parseInt(item.extra_charges) || 0) +
        (parseInt(item.gst) || 0) +
        (parseInt(item.customer_proposal) || 0)
        }`,
      Customer_Discount:
        item.discount !== 0 &&
          item.discount !== null &&
          item.discount !== undefined &&
          item.discount !== '' &&
          !isNaN(item.discount)
          ? `₹${parseInt(item.discount).toLocaleString('en-IN')}`
          : '₹0',
      status: item.approved ? 'Approved' : 'Approval',
      created_at: item.created_at ? item.created_at : '---'
    };
  };

  const transformedData = underConsiderationData?.data?.map((item) =>
    transformData(handleUnderConsiderationData(item), [
      'id',
      'name',
      'Unit_Name',
      'project',
      'Customer_Proposal',
      'Plc_Price',
      'GST',
      'Total_Cost',
      'Customer_Discount',
      'status',
      'created_at',
      'actions'
    ])
  );

  useEffect(() => {
    setFilteredData(transformedData);
  }, [underConsiderationData?.data]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleViewLeadDetails = (id) => {
    const leadId = underConsiderationData?.data?.find((item) => item.id === id)?.lead_id;
    if (leadId) {
      navigateWithReturn(navigate, `/builder/UCDetails/${leadId}`, {
        returnTo: buildReturnToFromLocation(location),
        state: { status: 'Under Consideration' }
      });
    } else {
      toast.error('Lead not found');
    }
  };

  const handleOnClickSearch = async () => {
    await refetchLeads();
  };

  const handleSearchChange = useCallback(
    async (incomingQuery) => {
      setPage(1);
      setSearchQuery(incomingQuery);

      // Track search
      mixpanel.track('Closed deal List Search', {
        user_id: userId,
        search_query: incomingQuery,
        project_id: projectID,
        timestamp: new Date().toISOString()
      });
    },
    [userId, projectID]
  );

  const handleApprove = (id) => {
    const data = underConsiderationData?.data?.find((item) => Number(item.id) === Number(id));
    if (data) {
      if (data.approved) {
        toast.error('Already Approved');
        return;
      }
    }
    const payload = {
      approved: true
    };
    handleAction({ id, payload });
  };

  const { mutate: handleAction } = useMutation({
    mutationFn: ({ id, payload }) => approveUnderConsideration(id, payload),
    onSuccess: async (data) => {
      toast.success(`Approved Successfully`);

      refetchData();
    },
    onError: (err) => {
      console.error(`Error Approving Item`, err);
      toast.error(err?.response?.data?.message || err?.message || 'Error Approving Item');
    }
  });

  const { mutate: handleCheckDelete } = useMutation({
    mutationFn: (id) => handleCheckUCDelete(id),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async (res, id) => {
      setIsSubmitting(false);
      const underConsideration = underConsiderationData?.data?.find(
        (item) => parseInt(item.id) === parseInt(id)
      );
      const underConsiderationName = underConsideration?.lead?.user?.name || 'Under Consideration';
      if (res.canDelete) {
        setCanDelete('yes');
        setResData({ name: underConsiderationName });
        setSelectedIds([parseInt(id)]);
        setIsSubmitting(false);
      } else {
        // Fallback if not found
        setResData({ ...res, name: underConsiderationName });
        setCanDelete('no');
        setIsSubmitting(false);
        setSelectedIds([parseInt(id)]);
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      setIsSubmitting(false);
      console.error('Error checking dependencies', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error checking dependencies');
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => deleteUnderConsideration(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success('Under Consideration Deleted Successfully');
      setSelectedIds([]);
      setSelectedItems([]);
      setCanDelete(null);
      setIsBulk(false);
      setSelectAll(false);
      refetchData();
    },
    onError: (err) => {
      setIsSubmitting(false);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error deleting under consideration'
      );
    }
  });

  const HandleDelete = (data) => {
    const payload = { ids: data };
    handleDeleteFn(payload);
  };

  const handleSelectAll = async (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);

    if (checked) {
      setIsSubmitting(true); // Start loader

      try {
        const promises = underConsiderationData?.data?.map((item) =>
          handleCheckUCDelete(item.id)
            .then((res) => {
              if (res.canDelete) return item.id;
              return null;
            })
            .catch(() => null)
        );

        const results = await Promise.all(promises);
        const validIds = results.filter((id) => id !== null);
        setSelectedItems(validIds);
      } finally {
        setIsSubmitting(false); // Stop loader
      }
    } else {
      setSelectedItems([]);
    }
  };

  const HandleFunctionCalls = async (id, checked) => {
    if (checked) {
      setIsSubmitting(true);
      try {
        const res = await handleCheckUCDelete(id);
        if (res.canDelete) {
          setIsSubmitting(false);
          setSelectedItems((prev) => [...prev, id]);
        } else if (!res.canDelete) {
          setIsSubmitting(false);
          toast.error(`Can not delete this under consideration because there is dependencies`);
        }
      } catch (err) {
        setIsSubmitting(false);
        console.error('Error checking dependencies', err);
        toast.error(err?.response?.data?.message || err?.message || 'Error checking dependencies');
      }
    } else {
      setIsSubmitting(false);
      setSelectedItems((prev) => prev.filter((item) => item !== id));
    }
  };

  const handleUnderConsideration = (data) => {
    createUnderConsiderationMutation(data);
  };

  const { mutate: createUnderConsiderationMutation } = useMutation({
    mutationFn: (data) => createUnderConsideration(data),
    onSuccess: () => {
      toast.success('Under Consideration created successfully');
      setIsCloseDealModalOpen(false);
      setIsSelectLeadModalOpen(false);
      setLeadId('');
      refetchData();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to create under consideration'
      );
    }
  });

  // Function to save filters to localStorage
  const saveFiltersToStorage = (filters) => {
    localStorage.setItem('underConsiderationListFilters', JSON.stringify(filters));
  };

  // Function to clear specific filter
  const clearFilter = (filterType) => {
    if (filterType === 'project') {
      setProjectID(null);
      const newFilters = { ...activeFilters, project: null };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    } else if (filterType === 'status') {
      setStatusFilter(null);
      const newFilters = { ...activeFilters, status: null };
      setActiveFilters(newFilters);
      saveFiltersToStorage(newFilters);
    }

    // Track filter clear
    mixpanel.track('Under Consideration List Filter Cleared', {
      user_id: userId,
      filter_type: filterType,
      timestamp: new Date().toISOString()
    });
  };

  const handleLeadSelect = (lead) => {
    setLeadId(lead);
    setIsSelectLeadModalOpen(false);
    setIsCloseDealModalOpen(true);
  };

  ////////////////////////
  const leadOptions = [
    { value: '', label: 'Select Lead', disabled: true, selected: true },
    ...(leadsData?.data
      ?.map((lead) => {
        const name = lead?.user?.name || 'Unnamed Lead';
        const isInactive = lead?.user && !lead.user.is_active;
        return {
          value: lead.id,
          label: isInactive ? `${name} (Inactive)` : name
        };
      }) || [])
  ];

  return (
    <div className="w-full flex flex-col bg-gray-100 pb-0 overflow-hidden">
      {/* SelectLeadModal */}
      {isSelectLeadModalOpen && (
        <Modal
          width={50}
          name="Select Lead"
          modalType="select-lead"
          onClose={() => {
            setIsSelectLeadModalOpen(false);
            setLeadId('');
            setValue('lead_id', '');
          }}>
          <div className="flex flex-col justify-between items-start gap-4">
            <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Select Lead</span>
          </div>
          <form className="flex flex-col gap-4 pt-6" onSubmit={(e) => e.preventDefault()}>
            <div className="flex justify-between items-center w-full">
              <FormInput
                width={100}
                label="Select Lead"
                register={register}
                register_key="lead_id"
                onDropdownChange={handleLeadSelect}
                onSearchChange={setLeadPickerSearch}
                type="dropdown-autofilter"
                options={leadOptions}
                errors={selectErrors.lead_id}
              />
              {/* <FormInput
                width={100}
                label={'Select Lead'}
                register={register}
                register_key={'lead_id'}
                onDropdownChange={handleLeadSelect}
                type={'dropdown'}
                options={leadOptions}
                errors={selectErrors.lead_id}
              /> */}
            </div>
            <div className="flex justify-end items-center mt-4">
              <div className="space-x-4 flex">
                <Button
                  type="button"
                  onClick={() => {
                    setIsSelectLeadModalOpen(false);
                    setLeadId('');
                  }}
                  displayType="secondary">
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={isSubmitting || isLeadsLoading}
                  onClick={() => {
                    setIsSelectLeadModalOpen(false);
                    setIsCloseDealModalOpen(true);
                  }}>
                  Select
                </Button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Close Deal Modal */}
      {isCloseDealModalOpen && (
        <Modal
          width={50}
          name="Create Under Consideration"
          confirmOnClose={true}
          isDirty={true}
          onClose={() => {
            setIsCloseDealModalOpen(false);
            setIsSelectLeadModalOpen(true);
          }}>
          <UnderConsiderationCreate
            onClose={() => {
              setIsCloseDealModalOpen(false);
              setIsSelectLeadModalOpen(true);
            }}
            onSubmit={handleUnderConsideration}
            isSubmitting={isSubmitting}
            selectedLead={leadId}
          />
        </Modal>
      )}

      <Loader isVisible={isSubmitting} />
      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${resData?.name || 'this under consideration'}'`}
          onClose={() => {
            setCanDelete(null);
            setIsBulk(false);
            setSelectAll(false);
            setSelectedItems([]);
            setSelectedIds([]);
          }}
          width={45}>
          <div className="flex flex-col justify-center align-center ">
            <p className="text-center">
              Looks like this under consideration has dependencies, please delete following stages
              to proceed
            </p>
            <div className="flex justify-start, align-middle flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {(() => {
                const reasons = Array.isArray(resData?.reason)
                  ? resData.reason
                  : resData?.reason
                    ? [resData.reason]
                    : [];
                return reasons.map((r, index) => (
                  <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                    {index + 1}.{r}
                    {index < reasons.length - 1 ? ', ' : ''}
                  </span>
                ));
              })()}
            </div>
          </div>
          <div className="flex flex-col justify-start items-center mt-4 w-[100%]  bg-[#f7f4d0] pt-[4px] pb-[4px] pl-2 pr-2 rounded-[4px]">
            <div className="flex justify-between w-[99%] items-center bg-white p-2 pt-1 pb-1">
              <p>Would you like to delete more under considerations ?</p>
              <div
                onClick={(e) => {
                  setIsBulk(!isBulk);
                  setSelectedItems([]);
                  setSelectAll(false);
                }}
                className="bg-amber-100 p-2 pt-1 pb-1 rounded-[5px] flex justify-center items-center gap-2 cursor-pointer">
                Bulk Delete
                <input
                  type="checkbox"
                  checked={isBulk}
                  readOnly
                  className="outline-none border-none"
                />
              </div>
            </div>

            {isBulk && (
              <div className="flex justify-end w-[100%] pr-[1.2rem] pt-3">
                <div className="flex gap-2">
                  <p>Select All: </p>
                  <input
                    className="outline-none border-none"
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </div>
              </div>
            )}
            {isBulk && (
              <div className="pl-[10px] w-[100%] pr-[19px] mt-2 mb-2 max-h-[14rem] overflow-auto">
                {filteredData &&
                  filteredData?.map((c, index) => (
                    <div key={index} className="flex justify-between items-center text-[#5b5b5b]">
                      {index + 1}. {c.name}
                      <input
                        className="outline-none border-none"
                        type="checkbox"
                        checked={selectedItems?.includes(c.id)}
                        disabled={selectedIds?.includes(c.id)} // Disable if already selected
                        onChange={(e) => HandleFunctionCalls(c.id, e.target.checked)}
                      />
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="reset"
              onClick={() => {
                setCanDelete(null);
                setIsBulk(false);
                setSelectAll(false);
                setSelectedItems([]);
                setSelectedIds([]);
              }}
              displayType="secondary">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                HandleDelete(selectedItems);
              }}
              disabled={isSubmitting || selectedItems.length === 0}
              displayType="primary-red">
              Delete
            </Button>
          </div>
        </Modal>
      )}

      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => {
            setCanDelete(null);
            setSelectedIds([]);
          }}
          width={45}>
          <DeleteConfirmationModal
            name={resData.name}
            title={'under consideration'}
            onClose={() => setCanDelete(null)}
            data={filteredData}
            type={'underConsideration'}
            setCanDelete={setCanDelete}
            onDeleteClick={HandleDelete}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
          />
        </Modal>
      )}

      <Headroom
        disable
        style={{
          position: 'fixed',
          top: '4.6rem',
          left: collapsed ? '76px' : '16.6%',
          right: '0',
          zIndex: 10,
          transition: 'all 0.3s ease-in-out'
        }}>
        <div className="flex items-center justify-between px-6 py-4 bg-white border-t-[2px] border-stone-100 shadow-sm transition-all duration-300 ease-in-out">
          <div className="flex flex-row gap-5 justify-start items-center">
            <Search
              onChange={handleSearchChange}
              query={searchQuery}
              onClick={handleOnClickSearch}
            />
            {/* Filter Indicator */}
            {(activeFilters.project || activeFilters.status) && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Filtering by:</span>
                <div className="flex items-center gap-1">
                  {activeFilters.project && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {activeFilters.project}
                      <button
                        onClick={() => clearFilter('project')}
                        className="ml-1 text-blue-600 hover:text-blue-800 font-bold text-sm">
                        ×
                      </button>
                    </span>
                  )}
                  {activeFilters.status && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {activeFilters.status}
                      <button
                        onClick={() => clearFilter('status')}
                        className="ml-1 text-blue-600 hover:text-blue-800 font-bold text-sm">
                        ×
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-5 cursor-pointer">
            <div>{/* <img className="" src={filter} alt="" /> */}</div>
            <Button onClick={() => setIsSelectLeadModalOpen(true)}>
              Create Under Consideration
            </Button>
          </div>
        </div>
      </Headroom>

      <GridWrapper width={100} height={100}>
        <div className="table-container mt-5">
          <style>{`
            .table-container .table-body {
              position: static !important;
              overflow-y: visible !important;
              max-height: none !important;
              height: auto !important;
              top: auto !important;
              padding-bottom: 0 !important;
            }
            .table-container .table-container {
              height: auto !important;
            }
            .table-container > div {
              height: auto !important;
            }
          `}</style>
          <Table
            headers={[
              'Lead Name',
              'Unit Name',
              {
                label: 'Project',
                dropdown: true,
                dropdownOptions: [
                  { id: '', value: '', label: 'Select Project', selected: true },
                  ...projectsForDropdown
                ],
                onDropdownSelect: (id) => {
                  setProjectID(id);

                  // Update active filters
                  const selectedProject = projects.find((p) => p.id === id);
                  const newFilters = {
                    ...activeFilters,
                    project: selectedProject?.name || null
                  };
                  setActiveFilters(newFilters);
                  saveFiltersToStorage(newFilters);

                  // Track filter change
                  mixpanel.track('Under Consideration List Filter Changed', {
                    user_id: userId,
                    filter_type: 'project',
                    project_id: id,
                    timestamp: new Date().toISOString()
                  });
                }
              },
              'Customer Proposal',
              'Revised Plc Price',
              'GST',
              'Listed Rate',
              'Initial Discount',
              {
                label: 'Status',
                dropdown: true,
                dropdownOptions: [
                  { id: '', value: '', label: 'Select Status', selected: true },
                  { id: 'true', value: true, label: 'Approved' },
                  { id: 'false', value: false, label: 'Not Approved' }
                ],
                onDropdownSelect: (value) => {
                  setStatusFilter(value === '' ? null : value);

                  // Update active filters
                  let statusLabel = null;
                  if (value === 'true') {
                    statusLabel = 'Approved';
                  } else if (value === 'false') {
                    statusLabel = 'Not Approved';
                  }

                  const newFilters = {
                    ...activeFilters,
                    status: statusLabel
                  };
                  setActiveFilters(newFilters);
                  saveFiltersToStorage(newFilters);

                  // Track filter change
                  mixpanel.track('Under Consideration List Filter Changed', {
                    user_id: userId,
                    filter_type: 'status',
                    status_value: value,
                    timestamp: new Date().toISOString()
                  });
                }
              },
              'Created At',
              'Action'
            ]}
            data={filteredData}
            identifier={'id'}
            handleRowClick={handleViewLeadDetails}
            onApprove={userDesignationLevel?.toLowerCase() === 'low' ? undefined : handleApprove}
            onDelete={handleCheckDelete}
            isDeleteEnable={true}
            isEditEnable={false}
          />
          {filteredData?.length > 0 && (
            <PaginationComponent
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              isFetching={isFetching}
            />
          )}
        </div>
      </GridWrapper>
    </div>
  );
};

export default UnderConsiderationList;
